import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { addDays, format, startOfWeek } from "date-fns";
import { it } from "date-fns/locale";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PrintHeader } from "@/components/PrintHeader";

const ymd = (d) => format(d, "yyyy-MM-dd");
const fine = (ora, min) => { const [h, m] = ora.split(":").map(Number); const t = h * 60 + m + (min || 60); return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };
const STATO = { completato: "Completato", annullato: "Annullato" };
const AREA = { tutti: "", officina: "Solo officina", noleggio: "Solo noleggio" };
const Th = ({ children, className = "" }) => <th className={`border-b-2 border-slate-900 py-1.5 pr-2 text-left text-[10px] font-bold uppercase tracking-wider ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`border-b border-slate-200 py-1.5 pr-2 align-top ${className}`}>{children}</td>;

const daAppuntamento = (a) => ({
  key: `a-${a.id}`, data: a.data, ora: a.ora, orario: `${a.ora}–${fine(a.ora, a.durata_min)}`, cliente: a.cliente_nome, tel: a.cliente_telefono,
  tipo: `${a.area === "noleggio" ? "Noleggio" : "Officina"} · ${a.tipo}`, bici: a.bici_desc, note: a.note, stato: STATO[a.stato], barrato: a.stato === "annullato",
});

const daNoleggio = (n) => [["inizio", "Ritiro", "riconsegna"], ["fine", "Riconsegna", "ritirato"]].map(([campo, cosa, altro]) => ({
  key: `n-${n.id}-${campo}`, data: n[campo].slice(0, 10), ora: n[campo].slice(11, 16), orario: n[campo].slice(11, 16), cliente: n.cliente_nome, tel: n.cliente_telefono,
  tipo: `Noleggio · ${cosa} ${n.numero ? `#${n.numero}` : ""}`.trim(), bici: n.bici_desc,
  note: `${altro === "riconsegna" ? "Riconsegna" : "Ritirato"} ${format(new Date(n[campo === "inizio" ? "fine" : "inizio"]), "d/MM HH:mm")}${n.note ? ` · ${n.note}` : ""}`,
  noleggio: true,
}));

const TabellaGiorno = ({ righe, giorno }) => (
  <div className="mb-5 break-inside-avoid" data-testid={`stampa-giorno-${ymd(giorno)}`}>
    <p className="mb-1 border-b border-slate-300 pb-1 font-heading text-sm font-bold"><span className="capitalize">{format(giorno, "EEEE d MMMM", { locale: it })}</span> <span className="font-normal text-slate-500">· {righe.length} prenotazion{righe.length === 1 ? "e" : "i"}</span></p>
    {righe.length ? (
      <div className="overflow-x-auto print:overflow-visible">
        <table className="w-full min-w-[560px] border-collapse print:min-w-0">
          <thead><tr><Th className="w-24">Orario</Th><Th>Cliente</Th><Th>Telefono</Th><Th>Tipo</Th><Th>Bici</Th><Th>Note</Th><Th className="w-8">✓</Th></tr></thead>
          <tbody>
            {righe.map((r) => (
              <tr key={r.key} className={r.barrato ? "text-slate-400 line-through" : ""} data-testid={`stampa-riga-${r.key}`}>
                <Td className="font-mono font-bold">{r.orario}</Td>
                <Td className="font-semibold">{r.cliente}{r.stato && <span className="ml-1 text-[10px] font-normal uppercase text-slate-500">({r.stato})</span>}</Td>
                <Td className="font-mono">{r.tel || "—"}</Td>
                <Td className={r.noleggio ? "font-semibold text-sky-800" : ""}>{r.tipo}</Td>
                <Td>{r.bici || "—"}</Td>
                <Td className="whitespace-pre-wrap">{r.note || ""}</Td>
                <Td><span className="inline-block h-3.5 w-3.5 border border-slate-500" /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : <p className="py-2 text-slate-400">Nessuna prenotazione.</p>}
  </div>
);

export default function AgendaStampa() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const vista = sp.get("vista") === "settimana" ? "settimana" : "giorno";
  const area = AREA[sp.get("area")] !== undefined ? sp.get("area") : "tutti";
  const base = sp.get("data") ? new Date(`${sp.get("data")}T00:00`) : new Date();
  const giorni = vista === "settimana" ? [...Array(7)].map((_, i) => addDays(startOfWeek(base, { weekStartsOn: 1 }), i)) : [base];
  const [righe, setRighe] = useState(null);
  const [neg, setNeg] = useState({});

  useEffect(() => {
    const da = ymd(giorni[0]), a = ymd(giorni[giorni.length - 1]);
    Promise.all([
      api.get("/appuntamenti", { params: { da, a } }),
      api.get("/noleggi", { params: { da: `${da}T00:00`, a: `${ymd(addDays(giorni[giorni.length - 1], 1))}T00:00` } }),
    ]).then(([ap, no]) => {
      const app = ap.data.filter((x) => area === "tutti" || (x.area || "officina") === area).map(daAppuntamento);
      const nol = area === "officina" ? [] : no.data.filter((n) => n.stato !== "annullato").flatMap(daNoleggio).filter((r) => r.data >= da && r.data <= a);
      setRighe([...app, ...nol].sort((x, y) => x.ora.localeCompare(y.ora)));
    });
    api.get("/impostazioni").then((r) => setNeg(r.data));
  }, [sp.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!righe) return <div className="py-20 text-center text-sm text-slate-500">Caricamento...</div>;
  const periodo = vista === "settimana"
    ? `${format(giorni[0], "d MMM", { locale: it })} – ${format(giorni[6], "d MMM yyyy", { locale: it })}`
    : format(base, "EEEE d MMMM yyyy", { locale: it });
  const attive = righe.filter((r) => !r.barrato);

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-[210mm] flex-wrap justify-between gap-2 px-4 print:hidden">
        <Button variant="outline" onClick={() => navigate("/agenda")} data-testid="print-back-button"><ArrowLeft className="mr-1 h-4 w-4" /> Torna all'agenda</Button>
        <Button onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700" data-testid="print-now-button"><Printer className="mr-1 h-4 w-4" /> Stampa</Button>
      </div>
      <div className="print-sheet mx-auto max-w-[210mm] bg-white p-10 text-[11px] text-slate-900 shadow-md print:max-w-none print:p-0 print:shadow-none" data-testid="print-sheet">
        <PrintHeader neg={neg} titolo={vista === "settimana" ? "Agenda settimanale" : "Agenda giornaliera"}>
          <p className="font-heading text-lg font-bold capitalize" data-testid="stampa-agenda-periodo">{periodo}</p>
          <p data-testid="stampa-agenda-conteggio">{attive.filter((r) => !r.noleggio).length} appuntamenti · {attive.filter((r) => r.noleggio).length} ritiri/riconsegne noleggio{AREA[area] ? ` · ${AREA[area]}` : ""}</p>
        </PrintHeader>
        <div className="mt-5">
          {giorni.map((g) => <TabellaGiorno key={ymd(g)} giorno={g} righe={righe.filter((r) => r.data === ymd(g))} />)}
        </div>
        <p className="mt-6 text-right text-[9px] text-slate-400">Stampato il {format(new Date(), "d/MM/yyyy HH:mm")}</p>
      </div>
    </div>
  );
}
